/**
 * Funding source for outflows (Paiements, Dépenses).
 * - register: reduces physical cash / electronic solde (Kasse)
 * - opening_capital: Startkapital / capital d'ouverture (hors solde)
 * - external: Fremdkapital hors caisse (hors solde, ≠ Startkapital)
 */

import { resolveSaleRegisterIncome } from '../domain/sales/checkout';

export const FUNDING_REGISTER = 'register';
export const FUNDING_EXTERNAL = 'external';
export const FUNDING_OPENING_CAPITAL = 'opening_capital';
export const FUNDING_MIXED = 'mixed';

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function entryTotalAmount(entry) {
  if (!entry) return 0;
  return toNumber(entry.amount ?? entry.totalAmount ?? entry.total ?? entry.payment?.amount);
}

/**
 * True when no register sales income yet (cash/electronic paid).
 * Credit-only sales do not end installation — solde stays funded by opening capital.
 */
export function isSetupPhase({ sales = [] } = {}) {
  return !(Array.isArray(sales) ? sales : []).some(
    (s) => resolveSaleRegisterIncome(s).amount > 0
  );
}

function normalizeFundingSource(value) {
  return String(value || '').trim().toLowerCase();
}

/** Startkapital / capital d'ouverture (incl. legacy setupPhasePayment tagged as external). */
export function isOpeningCapitalFundingEntry(entry) {
  if (!entry) return false;
  if (entry.openingCapitalPayment === true) return true;
  const fs = normalizeFundingSource(entry.fundingSource);
  if (fs === FUNDING_OPENING_CAPITAL) return true;
  // Legacy installation payments were stored as external + setupPhasePayment
  if (entry.setupPhasePayment === true && fs !== FUNDING_REGISTER && fs !== FUNDING_MIXED) {
    return true;
  }
  return false;
}

/** True Fremdkapital — not start capital, not register. */
export function isExternalFundingEntry(entry) {
  if (!entry) return false;
  if (isOpeningCapitalFundingEntry(entry)) return false;
  return normalizeFundingSource(entry.fundingSource) === FUNDING_EXTERNAL;
}

/**
 * Split an outflow between register (Kasse) and external (Fremdkapital).
 * Prefer resolveCrossMethodOutflowFunding + explicit fundingMode at write time.
 */
export function resolveOutflowFunding({
  amount,
  disponible = 0,
  isManager = false,
  isSetupPhase: setup = false,
  fundingMode = null
} = {}) {
  const amt = Math.max(0, toNumber(amount));
  const avail = Math.max(0, toNumber(disponible));
  const mode = normalizeFundingSource(fundingMode);

  if (amt <= 0) {
    return {
      registerPortion: 0,
      externalPortion: 0,
      openingCapitalPortion: 0,
      fundingSource: FUNDING_REGISTER,
      blocked: false,
      setupPhase: false
    };
  }

  if (mode === FUNDING_OPENING_CAPITAL) {
    return {
      registerPortion: 0,
      externalPortion: 0,
      openingCapitalPortion: amt,
      fundingSource: FUNDING_OPENING_CAPITAL,
      blocked: false,
      setupPhase: Boolean(setup),
      openingCapitalPayment: true
    };
  }

  if (mode === FUNDING_EXTERNAL) {
    return {
      registerPortion: 0,
      externalPortion: amt,
      openingCapitalPortion: 0,
      fundingSource: FUNDING_EXTERNAL,
      blocked: false,
      setupPhase: false
    };
  }

  // Installation: default to Startkapital (same rule as dashboard read remapping)
  if (setup) {
    return {
      registerPortion: 0,
      externalPortion: 0,
      openingCapitalPortion: amt,
      fundingSource: FUNDING_OPENING_CAPITAL,
      blocked: false,
      setupPhase: true,
      openingCapitalPayment: true
    };
  }

  // Manager: Zahlung aus Istkasse (physisch) — nicht dem Kassierer-Écart zugeordnet (siehe actorRole)
  if (isManager && amt > avail) {
    const registerPortion = avail;
    const externalPortion = amt - avail;
    return {
      registerPortion,
      externalPortion,
      openingCapitalPortion: 0,
      fundingSource:
        registerPortion > 0 && externalPortion > 0
          ? FUNDING_MIXED
          : externalPortion > 0
            ? FUNDING_EXTERNAL
            : FUNDING_REGISTER,
      blocked: false,
      setupPhase: false
    };
  }

  if (isManager) {
    return {
      registerPortion: amt,
      externalPortion: 0,
      openingCapitalPortion: 0,
      fundingSource: FUNDING_REGISTER,
      blocked: false,
      setupPhase: false
    };
  }

  if (amt <= avail) {
    return {
      registerPortion: amt,
      externalPortion: 0,
      openingCapitalPortion: 0,
      fundingSource: FUNDING_REGISTER,
      blocked: false,
      setupPhase: false
    };
  }

  if (!isManager) {
    return {
      registerPortion: 0,
      externalPortion: amt,
      openingCapitalPortion: 0,
      fundingSource: FUNDING_EXTERNAL,
      blocked: true,
      setupPhase: false
    };
  }

  return {
    registerPortion: amt,
    externalPortion: 0,
    openingCapitalPortion: 0,
    fundingSource: FUNDING_REGISTER,
    blocked: false,
    setupPhase: false
  };
}

/** Portion that reduces Kassen-Soll / disponible (Écart). */
export function getRegisterOutflowAmount(entry) {
  const total = entryTotalAmount(entry);
  if (total <= 0) return 0;

  if (isOpeningCapitalFundingEntry(entry) || isExternalFundingEntry(entry)) return 0;

  const allocations = Array.isArray(entry?.methodAllocations) ? entry.methodAllocations : null;
  if (allocations?.length > 0) {
    return allocations.reduce(
      (sum, row) => sum + Math.max(0, toNumber(row.registerPortion ?? row.amount)),
      0
    );
  }

  if (entry?.registerPortion != null && Number.isFinite(Number(entry.registerPortion))) {
    return Math.max(0, Math.min(total, Number(entry.registerPortion)));
  }

  const fs = normalizeFundingSource(entry?.fundingSource || FUNDING_REGISTER);
  if (fs === FUNDING_MIXED && entry?.externalPortion != null) {
    return Math.max(0, total - toNumber(entry.externalPortion));
  }
  return total;
}

/** Portion booked as Startkapital (does not affect solde). */
export function getOpeningCapitalOutflowAmount(entry) {
  const total = entryTotalAmount(entry);
  if (total <= 0) return 0;
  if (!isOpeningCapitalFundingEntry(entry)) return 0;
  if (entry?.openingCapitalPortion != null && Number.isFinite(Number(entry.openingCapitalPortion))) {
    return Math.max(0, Math.min(total, Number(entry.openingCapitalPortion)));
  }
  return total;
}

/** Portion booked as Fremdkapital (does not affect Écart / solde). */
export function getExternalOutflowAmount(entry) {
  const total = entryTotalAmount(entry);
  if (total <= 0) return 0;

  if (isOpeningCapitalFundingEntry(entry)) return 0;
  if (isExternalFundingEntry(entry)) return total;

  if (entry?.externalPortion != null && Number.isFinite(Number(entry.externalPortion))) {
    return Math.max(0, Math.min(total, Number(entry.externalPortion)));
  }

  const fs = normalizeFundingSource(entry?.fundingSource || FUNDING_REGISTER);
  if (fs === FUNDING_MIXED && entry?.registerPortion != null) {
    return Math.max(0, total - toNumber(entry.registerPortion));
  }
  return 0;
}

/**
 * Single owner for how an outflow hits solde / Attendu / financing columns.
 * Matches computeLiquidBalanceByMethod — including installation remapping of
 * untagged / register-tagged outflows onto opening capital.
 *
 * @param {object} entry
 * @param {{ installationPhase?: boolean }} [options]
 * @returns {{ registerAmt: number, externalAmt: number, openingCapitalAmt: number }}
 */
export function resolveLiquidOutflowPortions(entry, { installationPhase = false } = {}) {
  if (!entry) {
    return { registerAmt: 0, externalAmt: 0, openingCapitalAmt: 0 };
  }

  const total = Math.max(0, entryTotalAmount(entry));
  if (total <= 0) {
    return { registerAmt: 0, externalAmt: 0, openingCapitalAmt: 0 };
  }

  const openingAmt = getOpeningCapitalOutflowAmount(entry);
  const externalAmtTagged = getExternalOutflowAmount(entry);

  if (openingAmt > 0) {
    return { registerAmt: 0, externalAmt: 0, openingCapitalAmt: openingAmt };
  }

  if (externalAmtTagged > 0) {
    const externalAmt = externalAmtTagged;
    const registerAmt = Math.max(
      0,
      Math.min(getRegisterOutflowAmount(entry), total - externalAmt)
    );
    return { registerAmt, externalAmt, openingCapitalAmt: 0 };
  }

  if (installationPhase) {
    // Legacy untagged / register-tagged during installation → Startkapital
    return { registerAmt: 0, externalAmt: 0, openingCapitalAmt: total };
  }

  return {
    registerAmt: getRegisterOutflowAmount(entry),
    externalAmt: getExternalOutflowAmount(entry),
    openingCapitalAmt: getOpeningCapitalOutflowAmount(entry)
  };
}

export function attachFundingToOutflowRecord(record, funding) {
  if (!record || !funding) return record;
  const fs = normalizeFundingSource(funding.fundingSource);
  return {
    ...record,
    fundingSource: funding.fundingSource,
    registerPortion: Number(funding.registerPortion) || 0,
    externalPortion: Number(funding.externalPortion) || 0,
    openingCapitalPortion: Number(funding.openingCapitalPortion) || 0,
    ...(Array.isArray(funding.methodAllocations) && funding.methodAllocations.length > 0
      ? { methodAllocations: funding.methodAllocations }
      : {}),
    ...(fs === FUNDING_OPENING_CAPITAL || funding.openingCapitalPayment
      ? { openingCapitalPayment: true, setupPhasePayment: true }
      : {}),
    ...(funding.setupPhase && fs !== FUNDING_OPENING_CAPITAL ? { setupPhasePayment: true } : {})
  };
}

/** actorRole / workerId eines Ausgangs-Eintrags. */
export function getOutflowActorRole(entry) {
  const role = String(entry?.actorRole ?? entry?.payment?.actorRole ?? '').toLowerCase();
  if (role) return role;
  const wid = String(entry?.workerId ?? entry?.payment?.workerId ?? '').toLowerCase();
  if (wid === 'manager') return 'manager';
  return '';
}

export function getOutflowWorkerId(entry) {
  return String(entry?.workerId ?? entry?.payment?.workerId ?? '').trim();
}

export function isManagerOutflow(entry) {
  if (getOutflowActorRole(entry) === 'manager') return true;
  const wid = getOutflowWorkerId(entry).toLowerCase();
  return wid === 'manager' || wid.startsWith('manager_');
}

/**
 * Ausgabe gehört zur Schicht eines bestimmten Kassierers (nicht Manager, nicht anderer Worker).
 */
export function isAttributedToWorkerSession(entry, sessionWorkerId) {
  const sid = String(sessionWorkerId ?? '').trim();
  if (!sid) return false;
  if (isManagerOutflow(entry)) return false;

  const role = getOutflowActorRole(entry);
  const wid = getOutflowWorkerId(entry);

  if (role === 'worker') return wid === sid;
  // Legacy ohne actorRole: nur wenn workerId exakt zur Session passt
  if (!role) return wid === sid;
  return false;
}

/** Abgang aus der physischen Kasse — nur Schicht des Kassierers (Écart / disponible Worker). */
export function getWorkerSessionRegisterOutflow(entry, sessionWorkerId, options = {}) {
  if (!isAttributedToWorkerSession(entry, sessionWorkerId)) return 0;
  return resolveLiquidOutflowPortions(entry, options).registerAmt;
}

export function getWorkerSessionExternalOutflow(entry, sessionWorkerId, options = {}) {
  if (!isAttributedToWorkerSession(entry, sessionWorkerId)) return 0;
  return resolveLiquidOutflowPortions(entry, options).externalAmt;
}

/** Abgang aus der Kasse durch den Manager (Istkasse / Bilanz, nicht Écart Kassierer). */
export function getManagerRegisterOutflow(entry, options = {}) {
  if (!isManagerOutflow(entry)) return 0;
  return resolveLiquidOutflowPortions(entry, options).registerAmt;
}

export function getManagerExternalOutflow(entry, options = {}) {
  if (!isManagerOutflow(entry)) return 0;
  return resolveLiquidOutflowPortions(entry, options).externalAmt;
}

/** Gesamter Kassenabgang (Kassierer + Manager) für Istkasse Manager. */
export function getGeneralRegisterOutflow(entry, options = {}) {
  return resolveLiquidOutflowPortions(entry, options).registerAmt;
}

export function getGeneralExternalOutflow(entry, options = {}) {
  return resolveLiquidOutflowPortions(entry, options).externalAmt;
}
